import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import * as _ from "lodash-es";
import { ConfirmationService } from "primeng/api";
import { DialogService } from "primeng/dynamicdialog";
import { SubSink } from "subsink";

import { RosterSummaryLineService } from "../services/roster-summery-line.service";
import { IRosterCode } from "../shared/models/roster-code";
import { IRosterSummaryLine } from "../shared/models/roster/roster-summary-line";
import { ToastService } from "../shared/toast/toast.service";
import { RosterSummaryLineFormComponent } from "./roster-summary-line-form.component";
import { IEntity } from "../shared/models/entity";
import { IRosterProject } from "../shared/models/roster/roster-project";
import { IEmployer } from "../shared/models/employer";

@Component({
  selector: "pps-manage-roster-summary-line",
  templateUrl: "./manage-roster-summary-line.component.html",
  styles: [`
    .roster-summary-line .p-fieldset{
      border: 1px solid #aaaaaa !important;
      -webkit-box-shadow: 4px 4px 10px 0px rgba(64,64,64,0.3) !important;
      -moz-box-shadow:    4px 4px 10px 0px rgba(64,64,64,0.3) !important;
      box-shadow:         4px 4px 10px 0px rgba(64,64,64,0.3) !important;      
    }

    .roster-summary-line .p-fieldset.inactive .p-fieldset-legend-text{
      color: #ffaaaa;
    }

    .roster-summary-line .p-fieldset.active .p-fieldset-legend-text{
      font-weight: bold;
    }
  `],
  encapsulation: ViewEncapsulation.None
})
export class ManageRosterSummaryLineComponent implements OnInit{
  private subs = new SubSink();
  smallScreen: boolean = false;
  
  collapseAll: boolean = true;
  workGroupsDict: {[id: number]: IEntity} = {};

  @Input() rosterCodes: IRosterCode[] = [];
  @Input() workGroups: IEntity[] = [];
  @Input() employers: IEmployer[] = [];
  @Input() rosterSummaryLines: IRosterSummaryLine[] = [];
  @Input() rosterProjects: IRosterProject[] = [];

  constructor(
    private _confirm: ConfirmationService,
    private _toast: ToastService,
    private _dialog: DialogService,
    private _rosterSummaryLineService: RosterSummaryLineService,
    private breakpointObserver: BreakpointObserver){
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });          
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }   

  loadData(){
    this.workGroupsDict = {};

    this.workGroups = this.workGroups.filter(wg => wg.active);
    this.workGroups.forEach(rg => this.workGroupsDict[rg.id] = rg);
    this.workGroups = _.cloneDeep(this.workGroups).sort((a: IEntity,b: IEntity) => a.sortNo - b.sortNo);

    // sort by RosterProject first, then by active status, then by summary line title
    this.rosterSummaryLines = _.cloneDeep(this.rosterSummaryLines)
      .sort((a: IRosterSummaryLine,b: IRosterSummaryLine) => {
        if (a.rosterProjectId !== b.rosterProjectId){
          return a.rosterProjectId - b.rosterProjectId;
        }

        if (a.active !== b.active){
          return (b.active ? 1 : 0) - (a.active ? 1 : 0);
        }

        return a.sortNo - b.sortNo;
      });
    this.rosterCodes = this.rosterCodes.filter(rc => rc.canBeUsedInTimesheetBreakdownRule);
  }

  deleteRosterSummaryLine(line: IRosterSummaryLine){
    this._confirm.confirm({
      key: "rsl-dialog",
      header: `Delete Roster Summary Line`,
      message: `Please confirm that you want to delete [${line.title}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteRosterSummaryLineHelper(line)
    });
  }

  deleteRosterSummaryLineHelper(line: IRosterSummaryLine){
    this.subs.sink = this._rosterSummaryLineService.delete(line.id)
      .subscribe(res=>{
        if (res.succeed){
          this._toast.succeeded(`${line.title} has been removed from roster.`);
          let index = this.rosterSummaryLines.findIndex(rsl => rsl.id === line.id);
          if (index >= 0){
            this.rosterSummaryLines.splice(index, 1);
          }
        }else{
          this._toast.failed(`Failed to remove ${line.title}`);
        }
      });    
  }

  deleteGroup(rslIndex:number, rslId: number, workGroupId: number){
    let groupName = this.workGroupsDict[workGroupId].name;

    this._confirm.confirm({
      key: "rsl-dialog",
      header: `Detach Work Group`,
      message: `Please confirm that you want to detach [${groupName}] from [${this.rosterSummaryLines[rslIndex].title}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteGroupHelper(rslIndex, rslId, workGroupId)
    });
  }

  private deleteGroupHelper(rslIndex:number, rslId: number, groupId: number){
    let rsl = {
      id: rslId,
      groupIds: [groupId]
    };
    let groupName = this.workGroupsDict[groupId].name;
    
    this.subs.sink = this._rosterSummaryLineService.deleteGroup(rsl)
      .subscribe(res=>{
        if (res.succeed){
          this._toast.succeeded(`Work group ${groupName} detached.`);
          let index = this.rosterSummaryLines[rslIndex].groupIds.findIndex(id => id === groupId);
          if (index >= 0){
            this.rosterSummaryLines[rslIndex].groupIds.splice(index, 1);
          }
        }else{
          this._toast.failed(`Failed to detach ${groupName}`);
        }
      });
  }

  addOrUpdateRosterSummaryLine(rosterSummaryLine: IRosterSummaryLine){
    let create = rosterSummaryLine === null;

    let dialog = this._dialog.open(RosterSummaryLineFormComponent, {
      header: `Define Roster Summary`,
      styleClass: "size-md",
      data:{
        rosterSummaryLine: rosterSummaryLine || {id: 0, title: "", active: true, groupIds: [], rosterSummaryFormular: null, companyCode: ""},
        workGroups: this.workGroups,
        rosterCodes: this.rosterCodes,
        rosterProjects: this.rosterProjects,
        employers: this.employers
      },
      draggable: true
      //baseZIndex: -200,
      //autoZIndex: false
    });
    
    this.subs.sink = dialog.onClose.subscribe(
      result => {
        if (result){
          if (!create){
            this.subs.sink = this._rosterSummaryLineService.update(result)
              .subscribe(res => {
                if (res.succeed){
                  var index = this.rosterSummaryLines.findIndex(rt=>rt.id === res.result.id);
                  if (index !== -1){
                    this.rosterSummaryLines[index] = _.cloneDeep(res.result);
                  }

                  this._toast.succeeded(`${rosterSummaryLine.title} updated.`);
                }else{
                  this._toast.failed(`Add new roster summary line failed: ${res.errors.join(";")}`);
                }
              });
          }else{
            this.subs.sink = this._rosterSummaryLineService.add(result)
              .subscribe(res => {
                if (res.succeed){
                  this.rosterSummaryLines.unshift(_.cloneDeep(res.result));
                  this._toast.succeeded(`Roster type ${res.result.title} created.`);
                }else{
                  this._toast.failed(`Update failed: ${res.errors.join(";")}`);
                }
              });
          }
        }
      },
      reason => {
      }
    );
  }

  public getRosterProject(rosterProjectId: number): IRosterProject{
    return this.rosterProjects.find(rp => rp.id === rosterProjectId);
  }

  public getEmployersInCommaSeperatedString(employerIds: string){
    return this._rosterSummaryLineService
      .getEmployersByCommaSeperatedIds(employerIds, this.employers)
      .map(e=>e.employerName)
      .join(",");
  }
}